import { Component, OnInit, OnDestroy, Inject, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';
import { map, take, catchError } from 'rxjs/operators';

import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';

import { ConfigurationService } from '../../configuration.service';
import { ProjectLanguage } from '../../firestore-models/project-language';
import { Facility } from '../../firestore-models/facility';
import { ApiFacility } from '../../api-client-module';
import { FacilityAttribute } from '../../firestore-models/facility-attribute';

import { AppConst } from '../../app-const';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-select-facilities-dialog',
  templateUrl: './select-facilities-dialog.component.html',
  styleUrls: ['./select-facilities-dialog.component.css'],
  standalone: false,
})
export class SelectFacilitiesDialogComponent implements OnInit, OnDestroy {

  projectLanguages!: Array<ProjectLanguage>;

  private facilitiesCollection!: AngularFirestoreCollection<Facility>;
  facilitiesObservable!: Observable<Facility[]>;
  facilitiesSubscribe!: Subscription;
  private facilityAttributesCollection!: AngularFirestoreCollection<FacilityAttribute>;
  facilityAttributesObservable!: Observable<FacilityAttribute[]>;
  facilityAttributesSubscribe!: Subscription;
  projectId: string;

  attrCollectionName = AppConst.FIRESTORE_FACILITY_ATTR;

  displayedColumns: string[] = ['position', 'select', 'facility_id', 'attribute', 'name'];
  dataSource = new MatTableDataSource<Facility>();
  selection = new SelectionModel<Facility>(true, []);

  protected ATTR_SEARCH_WORD_PREFIX: string = 'facilityAttributes';
  facilityAttributes!: Array<FacilityAttribute>;

  searchFilter: any = {};

  projectBaseLang: string = 'ja';

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialogRef: MatDialogRef<SelectFacilitiesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ApiFacility,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
      this.projectId = this.configurationService.getProjectId();
    }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.setCustomFilter();
    this.projectLanguages = this.configurationService.getProject().languages!;
    // 施設取得
    this.facilitiesCollection = this.angularFirestore.collection<Facility>(
      AppConst.FIRESTORE_FACILITIES, ref => ref.where('project_id', '==', this.projectId).orderBy('facility_id', 'asc'));
    this.facilitiesObservable = this.facilitiesCollection.valueChanges({ idField: 'id' });
    this.facilitiesSubscribe = this.facilitiesObservable.subscribe(facilities => {
      this.dataSource.data = this.addFilterValue(facilities);
    });
    // 施設属性取得
    this.facilityAttributesCollection = this.angularFirestore.collection<FacilityAttribute>(
      this.attrCollectionName, ref => ref.where('project_id', '==', this.projectId).orderBy('attribute_id', 'asc'));
    this.facilityAttributesObservable = this.facilityAttributesCollection.valueChanges({ idField: 'id' });
    this.facilityAttributesSubscribe = this.facilityAttributesObservable.subscribe(facilityAttributes => {
      this.facilityAttributes = facilityAttributes;
    });
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
  }

  ngOnDestroy(): void {
    this.facilitiesSubscribe.unsubscribe();
    this.facilityAttributesSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
    this.selection.clear() :
    this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: Facility): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  selectFacilities() {
    let facilities: Array<ApiFacility> = new Array<ApiFacility>();
    for (const row of this.dataSource.data) {
      if (this.selection.isSelected(row)) {
        const facility: ApiFacility = {
          id: row.id,
          facility_id: row.facility_id,
          facility_attribute_id: row.facility_attribute_id,
          facility_names: row.facility_names,
          facility_descs: row.facility_descs,
          floor_ids: row.floor_ids,
          favicon_file: row.favicon_file,
          facility_file_link: row.facility_file_link,
          logo_files: row.logo_files,
          facility_files: row.facility_files,
          youtube_codes: row.youtube_codes,
          use_youtube: row.use_youtube,
          externallink_names: row.externallink_names,
          externallink_urls: row.externallink_urls,
          use_externallink: row.use_externallink,
          purchase_titles: row.purchase_titles,
          latitude: row.facility_geopoint.latitude,
          longitude: row.facility_geopoint.longitude,
          map_zoom_top: row.map_zoom_top,
          map_zoom_map: row.map_zoom_map,
          addresses: row.addresses,
          inquiries: row.inquiries,
          policies: row.policies,
          copyrights: row.copyrights,
          faqs: row.faqs,
          theme_color: row.theme_color,
          meta_keywords: row.meta_keywords,
          meta_description: row.meta_description,
          google_analytics_code: row.google_analytics_code,
          site_url: row.site_url,
          site_url_staging: row.site_url_staging,
          telephone_number: row.telephone_number,
          map_disp_type: row.map_disp_type,
          visible_good: row.visible_good,
          unvisible_breadcrumb: row.unvisible_breadcrumb,
          unvisible_guide_number: row.unvisible_guide_number,
          unvisible_floor_name: row.unvisible_floor_name,
          unvisible_reservation_facility_name: row.unvisible_reservation_facility_name,
          use_guide: row.use_guide,
          unvisible_enter_num: row.unvisible_enter_num,
          use_number_entry: row.use_number_entry,
          use_enter_num_title: row.use_enter_num_title,
          enter_num_titles: row.enter_num_titles,
          enter_num_helps: row.enter_num_helps,
          visible_enquete: row.visible_enquete,
          use_question: row.use_question,
          visible_enquete_thanks: row.visible_enquete_thanks,
          use_enquete_thanks_title: row.use_enquete_thanks_title,
          enquete_thanks_titles: row.enquete_thanks_titles,
          visible_ranking: row.visible_ranking,
          use_ranking: row.use_ranking,
          visible_shelter: row.visible_shelter,
          use_shelter: row.use_shelter,
          visible_paid_content: row.visible_paid_content,
          use_paid_content: row.use_paid_content,
          visible_all_map: row.visible_all_map,
          use_all_map: row.use_all_map,
          use_entry_form: row.use_entry_form,
          not_overlap_guide: row.not_overlap_guide,
          top_text_align_left : row.top_text_align_left,
          floor_text_align_left : row.floor_text_align_left,
          exhibit_text_align_left : row.exhibit_text_align_left,
          menu_style: row.menu_style,
          language_icon_type: row.language_icon_type || 'globe',
          voice_control_type: row.voice_control_type,
          splats_key_devices: row.splats_key_devices,
          project_id: row.project_id,
          enabled: row.enabled,
        };
        facilities.push(facility);
      }
    }
    this.dialogRef.close(facilities);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'facility_id':
          return compare(a.facility_id, b.facility_id, isAsc);
        case 'attribute':
          const aAttr = a.facility_attribute_id ? a.facility_attribute_id : '';
          const bAttr = b.facility_attribute_id ? b.facility_attribute_id : '';
          return compare(aAttr, bAttr, isAsc);
        case 'name':
          const aName = a.facility_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          const bName = b.facility_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          return compare(aName, bName, isAsc);
        default:
          return 0;
      }
    });
  }

  setCustomFilter() {
    // NOTE: AND検索
    this.dataSource.filterPredicate = (data: any, filter: any) => {
      let matchCount = 0;
      let keys = Object.keys(filter);
      for (const key of keys) {
        if (!filter[key]) {
          // フィルタに値がない場合は全抽出
          matchCount++;
        } else {
          if (data[key].trim().toLowerCase().indexOf(filter[key]) !== -1) {
            matchCount++;
          }
        }
      }
      return (keys.length === matchCount);
    };
  }

  addFilterValue(docs: Array<any>) :Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      for (const value of doc.facility_names) {
        filterValue += value.name + ' '
      }
      doc['filterValue'] = filterValue;
      doc['filterValueAttr'] = this.ATTR_SEARCH_WORD_PREFIX + doc.facility_attribute_id;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchFilter['filterValue'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

  applyFilterAttr(event: any) {
    this.selection.clear();
    let filterValue = '';
    if (event.value) {
      filterValue = this.ATTR_SEARCH_WORD_PREFIX + event.value;
    }
    this.searchFilter['filterValueAttr'] = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchFilter;
  }

}
